import * as Api from '@ViewModels';
import momentTz from 'moment-timezone';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getDefaultDateStringValue } from '../../../../../models/UiUtils';
import { useEmailCampaignCancelScheduleMutation, useEmailUpdateSendScheduleMutation } from '../../../../../queries';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../ConfirmationDialog';
import { WarningIcon } from '../../../svgs/icons/WarningIcon';
import { RescheduleSendModal } from '../RescheduleSend';

interface IProps {
	loggingCategory?: string;
	campaignToCancel?: Api.ICampaign;
	campaignToReschedule?: Api.ICampaign;
	onSuccessfullyCanceledSchedule?: (campaign: Api.ICampaign) => void;
	onSuccessfullyRescheduled?: (campaign: Api.ICampaign) => void;
	onCancelScheduleCancellation?: () => void;
	onCancelRescheduling?: () => void;
}
export function GroupEmailActions({
	campaignToCancel,
	campaignToReschedule,
	onSuccessfullyCanceledSchedule,
	onSuccessfullyRescheduled,
	onCancelScheduleCancellation,
	onCancelRescheduling,
	loggingCategory = 'GroupEmailActions',
}: IProps) {
	const { logApiError, logEvent } = useEventLogging(loggingCategory);
	const errorMessages = useErrorMessages();
	const cancelCampaignMutation = useEmailCampaignCancelScheduleMutation({
		onError: error => {
			logApiError('CancelCampaign-Error', error);
		},

		onSuccess: campaign => onSuccessfullyCanceledSchedule(campaign),
	});
	const updateSendScheduleMutation = useEmailUpdateSendScheduleMutation({
		onSuccess: (campaign, variables) => {
			onSuccessfullyRescheduled(campaign);

			toast.success(`Schedule updated to ${getDefaultDateStringValue(variables.schedule.startDate)}`);
		},
		onError: error => {
			errorMessages.pushApiError(error);

			logApiError('RescheduleCampaign-Error', error);
		},
	});
	const onCancelConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (canceled || !result || !result.isDestructive) {
			onCancelScheduleCancellation();
			return;
		}
		if (!cancelCampaignMutation.isPending) {
			cancelCampaignMutation.mutate({ campaignId: campaignToCancel.id });
			logEvent('CancelCampaign', {
				id: campaignToCancel.id,
				schedule: result,
			});
		}
	};
	const onRescheduleRequestClose = (result?: Api.IScheduledSend, cancel?: boolean) => {
		if (cancel || !result) {
			onCancelRescheduling();
			return;
		}
		if (updateSendScheduleMutation.isPending) {
			return;
		}
		updateSendScheduleMutation.mutate({
			campaignId: campaignToReschedule.id,
			schedule: result,
			timeZone: momentTz.tz.guess(),
		});
		logEvent('RescheduleCampaign', {
			id: campaignToReschedule.id,
			schedule: result,
		});
	};
	return (
		<>
			<RescheduleSendModal
				initialSchedule={
					!!campaignToReschedule && !!campaignToReschedule.schedule ? campaignToReschedule.schedule : null
				}
				modalProps={{
					isOpen: !!campaignToReschedule && !!campaignToReschedule.schedule,
					onRequestClose: onRescheduleRequestClose,
				}}
				numberOfEmailsToSend={campaignToReschedule ? campaignToReschedule.totalEmails : 0}
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: !!campaignToCancel,
					onRequestClose: onCancelConfirmationRequestClose,
				}}
				options={[
					{
						isDestructive: true,
						representedObject: true,
						title: 'Cancel Email',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				title='Are you sure you want to cancel this email?'
			/>
		</>
	);
}
